import { ONLINE_STATUS } from "@/constants";
import { MeAPI } from "@/services/api";
import { auth, firestore } from "@/services/firebase";
import { browserInfo } from "@/utils";
import {
  addDuration,
  toDateWithOffset,
} from "vue-chess-shared/utils/date-helper";
import { simplePlayer } from "vue-chess-shared/utils/player-helper";

export default {
  state: {
    accessToken: null,
    profile: null,
    stats: null,
    user: null, // for firebase Auth
    permission: null,
    error: null, // for firebase Auth
    onlinePlayers: [],
    onlineStatus: ONLINE_STATUS.ACTIVE,
    currentMatch: null,
    activeTournaments: [],
    subscriptions: [],
    localGames: [], // contains all game playing at local, similar to firestore games
    gameDataCopies: [], // contains copy of all games playing, doesn't matter local or firestore game.
    isIdle: false,
    isAway: false,
    isPlayingMatch: false,
    isPlayingTournament: false,
    isBusy: false,
    currentScenario: {
      topicId: null,
      topic: "",
      pgn: "",
      isPuzzle: false,
      number: 0,
      isCustom: false, // to handle custom scenarios
    },
    currentTopicId: null,
    scenarioState: {
      pgn: null,
      moveId: null,
      showMoveTable: true,
      orientation: "white",
      puzzleStatus: null, // null, 'skipped', 'correct', 'incorrect'
      solutionStatus: null, // -1, 0, 1: hidden, revealing, revealed
    },
    scenarioBoardSize: null,
  },

  getters: {
    getProfile(state) {
      return state.profile;
    },
    getPermission(state) {
      return state.permission ?? {};
    },
    getPlayer(state) {
      if (!state.profile) return null;
      return {
        ...simplePlayer(state.profile),
        stats: state.stats,
        browser: browserInfo(),
      };
    },
    getUserId(state) {
      if (!state.profile) return null;
      return state.profile.user_id;
    },
    getStats(state) {
      return state.stats;
    },
    getUser(state) {
      return state.user;
    },
    isUserAuth(state) {
      return !!state.user;
    },
    getError(state) {
      return state.error;
    },
    getOnlinePlayers(state) {
      return state.onlinePlayers;
    },
    getOnlineStatus(state) {
      return state.onlineStatus;
    },
    getCurrentMatch(state) {
      return state.currentMatch;
    },
    getActiveTournaments(state) {
      return state.activeTournaments;
    },
    getSubscriptions(state) {
      return state.subscriptions;
    },
    // get game data by match id
    getGameDataCopyByMatchId(state) {
      return (matchId) => {
        if (!matchId) {
          return {};
        }

        const gameCopy = state.gameDataCopies.find(
          (g) => g.matchId === matchId
        );

        if (!gameCopy) {
          return {};
        }

        return gameCopy.gameData;
      };
    },
    // get the local match by id
    getLocalMatchById(state) {
      return (matchId) => {
        if (!matchId) {
          return null;
        }

        const game = state.localGames.find((g) => g.matchId === matchId);

        if (!game) {
          return null;
        }

        return game.match;
      };
    },
    getLocalMovesByMatchId(state) {
      return (matchId) => {
        if (!matchId) {
          return null;
        }

        const game = state.localGames.find((g) => g.matchId === matchId);

        if (!game) {
          return null;
        }

        return game.moves;
      };
    },
    getIsIdle(state) {
      return state.isIdle;
    },
    getIsAway(state) {
      return state.isAway;
    },
    getIsPlayingMatch(state) {
      return state.isPlayingMatch;
    },
    getIsPlayingTournament(state) {
      return state.isPlayingTournament;
    },
    getIsBusy(state) {
      return state.isBusy;
    },
    getCurrentScenario: (state) => state.currentScenario,
    getCurrentTopicId: (state) => state.currentTopicId,
    getScenarioState: (state) => state.scenarioState,
    isCustomScenario: (state) => state.currentScenario.isCustom,
    getScenarioBoardSize: (state) => state.scenarioBoardSize,
    getCoachingOrientation: (state) => state.scenarioState.orientation,
    getShowMoveTable: (state) => state.scenarioState.showMoveTable,
  },

  mutations: {
    setAccessToken(state, payload) {
      state.accessToken = payload;
    },
    setProfile(state, payload) {
      state.profile = payload;
    },
    setStats(state, payload) {
      state.stats = payload;
    },
    setUser(state, payload) {
      state.user = payload;
    },
    setPermission(state, payload) {
      state.permission = payload;
    },
    setError(state, payload) {
      state.error = payload;
    },
    setOnlinePlayers(state, payload) {
      state.onlinePlayers = payload;
    },
    setOnlineStatus(state, payload) {
      state.onlineStatus = payload;
    },
    setCurrentMatch(state, payload) {
      state.currentMatch = payload;
    },
    setActiveTournaments(state, payload) {
      state.activeTournaments = payload;
    },
    setSubscriptions(state, payload) {
      state.subscriptions = payload;
    },
    setGameCopy(state, { matchId, gameData }) {
      const gameCopy = state.gameDataCopies.find((g) => g.matchId === matchId);

      if (gameCopy) {
        gameCopy.gameData = gameData;
      } else {
        state.gameDataCopies.push({
          matchId: matchId,
          gameData: gameData,
        });
      }
    },
    setLocalMatch(state, { matchId, payload }) {
      const game = state.localGames.find((g) => g.matchId === matchId);

      if (game) {
        game.match = {
          ...game.match,
          ...payload,
        };
      } else {
        payload.createdAt = toDateWithOffset(0);
        payload.userAction = {};
        state.localGames.push({
          matchId: matchId,
          match: payload,
          moves: [],
        });
      }
    },
    addLocalMove(state, { matchId, moveData }) {
      const game = state.localGames.find((g) => g.matchId === matchId);

      if (!game) {
        return;
      }

      // fake a random delay to stimulate delay between server and opponent
      const randomDelay = Math.random() * 500;
      const receivedAt = addDuration(new Date(), randomDelay);
      moveData.createdAt = toDateWithOffset(0);
      moveData.received = true;
      moveData.receivedAt = toDateWithOffset(0, receivedAt);
      moveData.userAction = {};
      game.moves.push(moveData);
    },
    updateMatchUserAction(state, { matchId, userAction }) {
      const game = state.localGames.find((g) => g.matchId === matchId);

      if (!game) {
        return;
      }

      const match = game.match;

      if (!match) {
        return;
      }

      match.userAction = {
        ...match.userAction,
        ...userAction,
      };
    },
    updateLastMoveUserAction(state, { matchId, userAction }) {
      const game = state.localGames.find((g) => g.matchId === matchId);

      if (!game) {
        return;
      }

      const lastMove = game.moves.at(-1);

      if (!lastMove) {
        return;
      }

      lastMove.userAction = {
        ...lastMove.userAction,
        ...userAction,
      };
    },
    setIsIdle(state, payload) {
      state.isIdle = payload;
    },
    setIsAway(state, payload) {
      state.isAway = payload;
    },
    setIsPlayingMatch(state, payload) {
      state.isPlayingMatch = payload;
    },
    setIsPlayingTournament(state, payload) {
      state.isPlayingTournament = payload;
    },
    setIsBusy(state, payload) {
      state.isBusy = payload;
    },
    setCurrentScenario(state, scenario) {
      state.currentScenario = {
        ...scenario,
        isCustom:
          !scenario.topicId ||
          ["Custom Example"].includes(scenario.topic?.title),
      };
      if (scenario.topicId) {
        state.currentTopicId = scenario.topicId;
      }
    },
    setCurrentTopicId(state, topicId) {
      state.currentTopicId = topicId;
    },
    setScenarioState(state, scenarioState) {
      // Merge the new scenarioState with the existing one to preserve any properties not included in the update
      state.scenarioState = { ...state.scenarioState, ...scenarioState };
    },

    setShowMoveTable(state, showMoveTable) {
      state.scenarioState.showMoveTable = showMoveTable;
      // Update the entire scenarioState to ensure it's synced
      state.scenarioState = { ...state.scenarioState };
    },
    setScenarioBoardSize(state, size) {
      state.scenarioBoardSize = size;
    },
    setCoachingOrientation(state, orientation) {
      state.scenarioState.orientation = orientation;
      // Update the entire scenarioState to ensure it's synced
      state.scenarioState = { ...state.scenarioState };
    },
  },

  actions: {
    queryProfileAction({ commit }) {
      MeAPI.getMyProfile().then((data) => {
        console.log("queryProfileAction", data ? data.user_id : data);
        if (!data) return console.error("Failed to get my profile");

        firestore
          .collection("permissions")
          .doc(data.username)
          .get()
          .then((snapshot) => {
            const permission = snapshot.data() || {};

            if (permission.admin) {
              data.isAdmin = true;
            }

            commit("setProfile", data);
            commit("setPermission", permission);
          });
      });
      MeAPI.getMySubscriptions().then((data) => {
        if (!data) return console.error("Failed to get my subscriptions");
        commit("setSubscriptions", data);
        if (!data.length)
          window.location = "https://www.chesssquad.com/subscribe";
      });
    },
    //TODO: firebase auth
    signInWithTokenAction({ commit }, payload) {
      return auth
        .signInWithCustomToken(payload)
        .then((response) => {
          commit("setUser", response.user);
          commit("setAccessToken", payload);
        })
        .catch((error) => {
          commit("setError", error.message);
        });
    },
    signOutAction({ commit }) {
      auth
        .signOut()
        .then(() => {
          commit("setUser", null);
          commit("setAccessToken", null);
        })
        .catch((error) => {
          commit("setError", error.message);
        });
    },
    authAction({ commit }) {
      auth.onAuthStateChanged((user) => {
        if (user) {
          commit("setUser", user);
        } else {
          commit("setUser", null);
        }
      });
    },
    setCurrentMatch({ commit }, payload) {
      commit("setCurrentMatch", payload);
    },
    saveGameCopy({ commit }, payload) {
      commit("setGameCopy", payload);
    },
    saveLocalMatch({ commit }, payload) {
      commit("setLocalMatch", payload);
    },
    async addLocalMove({ commit }, payload) {
      commit("addLocalMove", payload);
    },
    updateMatchUserAction({ commit }, payload) {
      commit("updateMatchUserAction", payload);
    },
    updateLastMoveUserAction({ commit }, payload) {
      commit("updateLastMoveUserAction", payload);
    },
    setOnlineStatus({ commit }, payload) {
      commit("setOnlineStatus", payload);
    },
    setIsIdle({ commit }, payload) {
      commit("setIsIdle", payload);
    },
    setIsAway({ commit }, payload) {
      commit("setIsAway", payload);
    },
    setIsPlayingMatch({ commit }, payload) {
      commit("setIsPlayingMatch", payload);
    },
    setIsPlayingTournament({ commit }, payload) {
      commit("setIsPlayingTournament", payload);
    },
    setIsBusy({ commit }, payload) {
      commit("setIsBusy", payload);
    },
    updateScenarioBoardSize({ commit }, size) {
      commit("setScenarioBoardSize", size);
    },
    updateCoachingOrientation({ commit }, orientation) {
      commit("setCoachingOrientation", orientation);
    },
    setShowMoveTable({ commit }, showMoveTable) {
      commit("setShowMoveTable", showMoveTable);
    },
  },
};
